import { createEntityAdapter } from '@reduxjs/toolkit';

import { WorkScheduleCalendar } from '@vk-hr-tek/core/calendar/gen/calendar';
import { AppError } from '@vk-hr-tek/core/error';

import {
  Link,
  QuickActionCreateEvent,
  Widget,
  CalendarWidgetCompany,
  PersonalLink,
  GetUserWidgetExternalResponse,
  WidgetPartitionView,
  GetUserWidgetBannerResponse,
  GetWidgetNewsResponse,
  GetWidgetSurveysResponse,
  QuickActionLink,
  AbsenceEmployeeItem,
  WidgetTopNewsItem,
  PostsFeedNewsData,
  FeedPostData,
  WidgetPartitionGrid,
  GetUserWidgetBirthdaysResponse,
} from '@app/gen/dashboard';
import { RequestStatus, DynamicEntity } from '@app/types';
import { ManageableCompanyItem } from '@app/gen/users';

export interface External extends GetUserWidgetExternalResponse {
  status: RequestStatus;
}

interface Banner extends GetUserWidgetBannerResponse {
  status: RequestStatus;
}

// Значения по умолчанию для размеров сетки
const DEFAULT_PARTITIONS_COLS = 2;
const DEFAULT_PARTITIONS_ROWS = 5;

export interface DashboardState {
  status: RequestStatus;
  error: AppError | null;
  layout: Widget[];
  widgetsSettings: {
    status: RequestStatus;
    widgets: Widget[];
    error: AppError | null;
  };
  absences: {
    status: RequestStatus;
    isManager: boolean;
    items: AbsenceEmployeeItem[];
  };
  quickActions: {
    status: RequestStatus;
    companyActions: QuickActionCreateEvent[];
    userActions: QuickActionLink[];
    error: AppError | null;
  };
  electronicServices: {
    status: RequestStatus;
    items: Link[];
  };
  banner: Record<string, Banner>;
  personalLinks: {
    status: RequestStatus;
    items: PersonalLink[];
  };
  employeePartitions: {
    status: RequestStatus;
    items: WidgetPartitionView[];
    rows: number;
    cols: number;
  };
  companyPartitions: {
    status: RequestStatus;
    items: WidgetPartitionView[];
    cols: WidgetPartitionGrid['cols'];
    rows: WidgetPartitionGrid['rows'];
    manageableCompanies: ManageableCompanyItem[];
  };
  external: Record<string, External>;
  taskList: {
    status: RequestStatus;
    items: WidgetPartitionView[];
  };
  newsList: {
    status: RequestStatus;
    items: GetWidgetNewsResponse['news'];
  };
  calendar: {
    status: RequestStatus;
    companies: CalendarWidgetCompany[];
    companyId: string | undefined;
    includedEmployees: string[];
    workSchedule: WorkScheduleCalendar | null;
  };
  personalLinksSettings: {
    status: RequestStatus;
    error: AppError | null;
    name: string | undefined;
    links: PersonalLink[];
  };
  newsSettings: {
    status: RequestStatus;
    error: AppError | null;
    name: string | undefined;
    hasFilter: boolean;
    isFilterAvailable: boolean;
  };
  birthdays: {
    status: RequestStatus;
    data: GetUserWidgetBirthdaysResponse | null;
  };
  surveys: {
    status: RequestStatus;
    items: GetWidgetSurveysResponse['surveys'];
  };
  surveysSettings: {
    status: RequestStatus;
    error: AppError | null;
    name: string | undefined;
  };
  quickActionsSettings: {
    status: RequestStatus;
    error: AppError | null;
    companyLinks: QuickActionCreateEvent[];
    userLinks: QuickActionLink[];
  };
  topNews: {
    status: RequestStatus;
    error: AppError | null;
    items: WidgetTopNewsItem[];
  };
  feedPostsList: {
    status: RequestStatus;
    error: AppError | null;
    total: number;
    posts: (PostsFeedNewsData | FeedPostData)[];
    avatars: {
      status: RequestStatus;
      list: Record<string, string>;
    };
  };
}

export const initialState: DashboardState = {
  status: 'idle',
  error: null,
  layout: [],
  widgetsSettings: {
    status: 'idle',
    widgets: [],
    error: null,
  },
  absences: {
    isManager: false,
    items: [],
    status: 'idle',
  },
  quickActions: {
    status: 'idle',
    companyActions: [],
    userActions: [],
    error: null,
  },
  electronicServices: {
    status: 'idle',
    items: [],
  },
  banner: {},
  personalLinks: {
    status: 'idle',
    items: [],
  },
  employeePartitions: {
    status: 'idle',
    items: [],
    rows: 0,
    cols: 0,
  },
  companyPartitions: {
    status: 'idle',
    items: [],
    cols: DEFAULT_PARTITIONS_COLS,
    rows: DEFAULT_PARTITIONS_ROWS,
    manageableCompanies: [],
  },
  external: {},
  taskList: {
    status: 'idle',
    items: [],
  },
  newsList: {
    status: 'idle',
    items: [],
  },
  calendar: {
    status: 'idle',
    companies: [],
    companyId: undefined,
    includedEmployees: [],
    workSchedule: null,
  },
  personalLinksSettings: {
    status: 'idle',
    error: null,
    name: undefined,
    links: [],
  },
  newsSettings: {
    status: 'idle',
    error: null,
    name: '',
    hasFilter: false,
    isFilterAvailable: false,
  },
  birthdays: {
    status: 'idle',
    data: null,
  },
  surveys: {
    status: 'idle',
    items: [],
  },
  surveysSettings: {
    status: 'idle',
    error: null,
    name: undefined,
  },
  quickActionsSettings: {
    status: 'idle',
    error: null,
    companyLinks: [],
    userLinks: [],
  },
  topNews: {
    status: 'idle',
    error: null,
    items: [],
  },
  feedPostsList: {
    status: 'idle',
    error: null,
    posts: [],
    total: 0,
    avatars: {
      status: 'idle',
      list: {},
    },
  },
};

export const dashboardAdapter = createEntityAdapter<DynamicEntity>();
